import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Alert,
    Tooltip,
    LinearProgress,
    type SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import Swal from 'sweetalert2';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIButton } from '../../atomic/atoms';
import { CustomerCommand } from '../../services/command';
import TimeHelper from '../../utils/time';
import { CDX, cdxCls } from './customerDiagnostics.classes';

export type CustomerDiagnosticsParameterType = 'CustomerId' | 'Email' | 'Phone';

export interface CustomerDiagnosticsTimelineRow {
    id: string;
    date: string;
    flow: string;
    apiVersion: string;
    logType: string;
    errorCategory: string | null;
    description: string | null;
    actionRequired: string | null;
    isError: boolean;
    ipAddress: string | null;
    platform: string | null;
    userAgent: string | null;
    deviceId: string | null;
    extraData: unknown;
}

export interface CustomerDiagnosticsSummary {
    totalEvents: number;
    errorCount: number;
    errorsByCategory: Record<string, number>;
    errorsByFlow: Record<string, number>;
    firstEventDate: string | null;
    lastEventDate: string | null;
}

export interface CustomerDiagnosticsResponse {
    operationStatus?: boolean;
    errorMessageKeyword?: string | null;
    timeline?: CustomerDiagnosticsTimelineRow[];
    summary?: CustomerDiagnosticsSummary | null;
}

/** Request body: only the selected lookup field is sent. */
function buildDiagnosticsRequest(
    parameterType: CustomerDiagnosticsParameterType,
    parameterValue: string
): Record<string, string | number> {
    const v = parameterValue.trim();
    if (parameterType === 'CustomerId') {
        return { customerId: parseInt(v, 10) };
    }
    if (parameterType === 'Email') {
        return { email: v };
    }
    return { phoneNumber: v };
}

const formatExtraData = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

export const CustomerDiagnostics: FC = () => {
    const { t } = useTranslation();
    const [parameterType, setParameterType] = useState<CustomerDiagnosticsParameterType>('Email');
    const [parameterValue, setParameterValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [rows, setRows] = useState<CustomerDiagnosticsTimelineRow[]>([]);
    const [summary, setSummary] = useState<CustomerDiagnosticsSummary | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const handleParameterTypeChange = (event: SelectChangeEvent<CustomerDiagnosticsParameterType>) => {
        setParameterType(event.target.value as CustomerDiagnosticsParameterType);
        setParameterValue('');
    };

    const parameterPlaceholder =
        parameterType === 'CustomerId'
            ? t('customerDiagnostics.parameterPlaceholderCustomerId')
            : t('customerDiagnostics.parameterPlaceholder');

    const fetchDiagnostics = useCallback(async () => {
        const trimmed = parameterValue.trim();
        if (!trimmed) {
            Swal.fire(t('general.error'), t('customerDiagnostics.valueRequired'), 'warning');
            return;
        }

        if (parameterType === 'CustomerId') {
            const customerId = parseInt(trimmed, 10);
            if (!Number.isFinite(customerId) || customerId <= 0 || String(customerId) !== trimmed) {
                Swal.fire(t('general.error'), t('customerDiagnostics.invalidCustomerId'), 'warning');
                return;
            }
        }

        setLoading(true);
        setFetchError(null);
        const payload = buildDiagnosticsRequest(parameterType, trimmed);

        try {
            const res = (await CustomerCommand.GetCustomerDiagnostics(payload)) as CustomerDiagnosticsResponse;

            if (res?.operationStatus === false) {
                const msg: any = res.errorMessageKeyword || t('customerDiagnostics.fetchFailed');
                setFetchError(msg);
                setRows([]);
                setSummary(null);
                return;
            }

            setFetchError(null);
            setHasSearched(true);
            const timeline = Array.isArray(res?.timeline) ? res.timeline : [];
            setRows(timeline);
            setSummary(res?.summary ?? null);
        } catch {
            setFetchError(t('customerDiagnostics.fetchFailed'));
            setRows([]);
            setSummary(null);
            Swal.fire(t('general.error'), t('customerDiagnostics.fetchFailed'), 'error');
        } finally {
            setLoading(false);
        }
    }, [parameterType, parameterValue, t]);

    const columns: GridColDef<CustomerDiagnosticsTimelineRow>[] = useMemo(
        () => [
            {
                field: 'date',
                headerName: t('customerDiagnostics.columns.date'),
                minWidth: 170,
                flex: 1,
                renderCell: (params: any) => (params.value ? TimeHelper.formatDateHourSecond(params.value) : ''),
            },
            {
                field: 'flow',
                headerName: t('customerDiagnostics.columns.flow'),
                minWidth: 160,
                flex: 1,
            },
            {
                field: 'apiVersion',
                headerName: t('customerDiagnostics.columns.apiVersion'),
                minWidth: 90,
                flex: 0.5,
            },
            {
                field: 'logType',
                headerName: t('customerDiagnostics.columns.logType'),
                minWidth: 200,
                flex: 1.2,
                renderCell: (params) => {
                    const text = params.value ?? '';
                    return (
                        <Tooltip title={text}>
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    py: 1,
                                }}
                            >
                                {text || '—'}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
            {
                field: 'errorCategory',
                headerName: t('customerDiagnostics.columns.errorCategory'),
                minWidth: 140,
                flex: 0.8,
            },
            {
                field: 'isError',
                headerName: t('customerDiagnostics.columns.isError'),
                minWidth: 100,
                flex: 0.5,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Chip
                        size="small"
                        label={params.value ? t('customerDiagnostics.error') : t('customerDiagnostics.ok')}
                        color={params.value ? 'error' : 'success'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                        className={CDX.chipTimelineStatus}
                    />
                ),
            },
            {
                field: 'description',
                headerName: t('customerDiagnostics.columns.description'),
                minWidth: 280,
                flex: 1.5,
                renderCell: (params: any) => {
                    const text = params.value ?? '';
                    return (
                        <Tooltip title={text}>
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    py: 1,
                                }}
                            >
                                {text || '—'}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
            {
                field: 'actionRequired',
                headerName: t('customerDiagnostics.columns.actionRequired'),
                minWidth: 140,
                flex: 0.8,
            },
            {
                field: 'ipAddress',
                headerName: t('customerDiagnostics.columns.ipAddress'),
                minWidth: 120,
                flex: 0.7,
            },
            {
                field: 'platform',
                headerName: t('customerDiagnostics.columns.platform'),
                minWidth: 110,
                flex: 0.7,
            },
            {
                field: 'userAgent',
                headerName: t('customerDiagnostics.columns.userAgent'),
                minWidth: 200,
                flex: 1,
                renderCell: (params) => {
                    const text = params.value ?? '';
                    return (
                        <Tooltip title={text}>
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    py: 1,
                                }}
                            >
                                {text || '—'}
                            </Typography>
                        </Tooltip>
                    );
                },
            },

            {
                field: 'extraData',
                headerName: t('customerDiagnostics.columns.extraData'),
                minWidth: 160,
                flex: 1,
                valueGetter: (_value, row) => formatExtraData(row.extraData),
                renderCell: (params) => {
                    const text = String(params.value ?? '');
                    return (
                        <Tooltip title={text}>
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    py: 1,
                                }}
                            >
                                {text || '—'}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
        ],
        [t]
    );

    return (
        <SCUIContent className={`${CDX.page} `}>
            <Grid container spacing={2} sx={{ mb: 2 }} className={CDX.breadcrumbRow}>
                <SCUIBreadCrumb title={t('customerDiagnostics.title')} componentLink="customerDiagnostics" />
            </Grid>

            <Card variant="outlined" className={CDX.filterCard} sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden' }}>
                {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
                <CardContent>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={{ xs: 12, md: 2 }}>
                            <FormControl fullWidth size="small" className={CDX.fieldParameterType}>
                                <InputLabel id="customer-diagnostics-param-type-label">
                                    {t('customerDiagnostics.parameterType')}
                                </InputLabel>
                                <Select<CustomerDiagnosticsParameterType>
                                    labelId="customer-diagnostics-param-type-label"
                                    value={parameterType}
                                    label={t('customerDiagnostics.parameterType')}
                                    onChange={handleParameterTypeChange}
                                    className={cdxCls(CDX.input, CDX.inputSelect, CDX.fieldParameterType)}
                                >
                                    <MenuItem value="CustomerId">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon fontSize="small" color="action" />
                                            {t('customerDiagnostics.types.customerId')}
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="Email">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <EmailIcon fontSize="small" color="action" />
                                            {t('customerDiagnostics.types.email')}
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="Phone">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PhoneIcon fontSize="small" color="action" />
                                            {t('customerDiagnostics.types.phone')}
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('customerDiagnostics.parameterValue')}
                                value={parameterValue}
                                className={cdxCls(CDX.input, CDX.inputTextfield, CDX.fieldParameterValue)}
                                onChange={(e) => setParameterValue(e.target.value)}
                                placeholder={parameterPlaceholder}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        fetchDiagnostics();
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 'auto' }}>
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="m"
                                text={loading ? t('customerDiagnostics.loading') : t('customerDiagnostics.fetch')}
                                showStartIcon
                                startIcon={<SearchIcon />}
                                onClick={fetchDiagnostics}
                                disabled={loading}
                                buttonType="button"
                                className={cdxCls(CDX.input, CDX.inputButton, CDX.btnFetch)}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {fetchError && (
                <Alert severity="warning" sx={{ mb: 2 }} className={CDX.alertError}>
                    {fetchError}
                </Alert>
            )}

            {summary && (
                <Card variant="outlined" className={CDX.summaryCard} sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            {t('customerDiagnostics.summaryTitle')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('customerDiagnostics.summary.totalEvents')}
                                </Typography>
                                <Typography variant="h6">{summary.totalEvents}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('customerDiagnostics.summary.errorCount')}
                                </Typography>
                                <Typography variant="h6" color="error.main">
                                    {summary.errorCount}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    {t('customerDiagnostics.summary.dateRange')}
                                </Typography>
                                <Typography variant="body2">
                                    {summary.firstEventDate
                                        ? TimeHelper.formatDateHourSecond(summary.firstEventDate)
                                        : '—'}{' '}
                                    →{' '}
                                    {summary.lastEventDate
                                        ? TimeHelper.formatDateHourSecond(summary.lastEventDate)
                                        : '—'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {hasSearched && (
                <Card variant="outlined" className={CDX.timelineCard} sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <Box sx={{ px: 2, pt: 2, pb: 1 }} className={CDX.timelineHeader}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('customerDiagnostics.timelineTitle')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('customerDiagnostics.timelineHint')}
                            </Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: 560, px: 2, pb: 2 }} className={CDX.datagrid}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                loading={loading}
                                getRowId={(row) => row.id}
                                disableRowSelectionOnClick
                                pageSizeOptions={[10, 25, 50, 100]}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 25, page: 0 } },
                                }}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.06)'
                                                : 'rgba(0,0,0,0.04)',
                                    },
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}
        </SCUIContent>
    );
};
